import * as React from 'react';
import { Dropdown as BootstrapDropdown } from 'react-bootstrap';

import { isNullOrUndefined } from '@acceligentllc/shared/utils/extensions';

import { bemBlock } from 'af-utils/bem.util';

import { Button } from '@acceligentllc/storybook';

import Label from 'af-components/LockedValue/Label';
import type { OwnProps as TooltipProps } from 'af-components/Tooltip';

import type { FlatListProps } from './DropdownMenu/FlatListMenu';
import FlatListMenu from './DropdownMenu/FlatListMenu';
import type { SectionListProps, SectionType } from './DropdownMenu/SectionListMenu';
import SectionListMenu from './DropdownMenu/SectionListMenu';
import Checkbox from './Shared/Checkbox';
import styles from './styles.module.scss';

export type DropdownOptionType = {
	[key: string]: any; // eslint-disable-line @typescript-eslint/no-explicit-any
	disabled?: boolean;
	onClick?: () => void;
};

type ControlledMultiSelect<T extends DropdownOptionType> = {
	controlledMultiSelect: true;
	currentSelections: Nullable<T>[];
	setCurrentSelections: (newSeletion: Nullable<T>[]) => void;
};

type NonControlledMultiSelect = {
	controlledMultiSelect?: false;
	currentSelections?: never;
	setCurrentSelections?: never;
};

type MultiSelectDropdown<T extends DropdownOptionType> = {
	isMultiselect: true;
	onMultiselectChange: (options: Nullable<T>[]) => void;
	onValueChange?: never;
	/*
	 * initially selected values
	 * */
	defaultSelections?: Nullable<T>[];
	autoApplySelections?: boolean;
} & (ControlledMultiSelect<T> | NonControlledMultiSelect);

interface SingleSelectDropdown {
	isMultiselect?: false;
	onMultiselectChange?: never;
	defaultSelections?: never;
	autoApplySelections?: never;
}
interface DropdownStyle {
	top: number | 'auto';
	bottom: number | 'auto';
	left: number;
	width: number | 'auto';
	a?: number & string;
}

export interface PropsWithLabel<T extends DropdownOptionType> extends SharedOwnProps<T> {
	labelKey: KeyOfType<T, string>;
}

export type Section<T extends DropdownOptionType> = SectionType<T>;

export interface PropsWithCustomRender<T extends DropdownOptionType> extends SharedOwnProps<T> {
	renderMenuItem?: (option: Nullable<T>, searchText?: string, index?: number) => React.JSX.Element;
	renderSelected?: (option: Nullable<T>) => React.JSX.Element;
}

interface SharedOwnProps<T extends DropdownOptionType> {
	id?: string;
	valueKey?: keyof T;
	className?: string;
	inputClassName?: string;
	containerClassName?: string;
	defaultValue?: Nullable<T>;
	disabled?: boolean;
	dropdownClassName?: string;
	dropdownId?: string;
	/** Enables search */
	filterable?: boolean;
	/** Function that filters options according to user's input text in the search box */
	filterBy?: (keyof T)[] | readonly (keyof T)[] | ((option: Nullable<T>, searchText: string) => boolean);
	/** Function that filters options at the mounting of Dropdown component, **not used if section list** */
	filterOptions?: (option: T, index: number) => boolean;
	fixed?: boolean;
	fullWidth?: boolean;
	hasBlankOption?: boolean;
	isButtonFullWidth?: boolean;
	isMenuItemUnlimited?: boolean;
	isWhite?: boolean;
	label?: string;
	menuItemClassName?: string;
	onClear?: () => void;
	onClearSearchText?: () => void;
	onClose?: () => void;
	onFocus?: () => void;
	onLazyLoad?: (isLazyLoaded: boolean) => Promise<void>;
	onMenuOpen?: () => void;
	onValueChange?: (selectedOption: Nullable<T>, selectedValue: string) => Promise<void> | void;
	placeholder?: string | React.JSX.Element;
	tooltipMessage?: TooltipProps['message'];
	withCaret?: boolean;
	withBorder?: boolean;
	forcePlaceholder?: boolean;
	isActionDropdown?: boolean;
	isControlled?: boolean;
	/** handle opening of dropdown from outside of component */
	open?: boolean;
	/** auto-open on initialization */
	autoOpen?: boolean;
	blankOption?: string | React.JSX.Element;
	shouldScrollToSelectedItem?: boolean;
	selectBtnLabel?: string;
	getSelectedValue?: () => unknown;
}

type DropdownActionOption = {
	hasActionOption: true;
	actionText: string;
	onActionClick: () => void;
} | {
	hasActionOption?: false;
};

export type OwnProps<T extends DropdownOptionType> =
	(PropsWithLabel<T> | PropsWithCustomRender<T>)
	& (SectionListProps<T> | FlatListProps<T>)
	& DropdownActionOption
	& (SingleSelectDropdown | MultiSelectDropdown<T>);

type Props<T extends DropdownOptionType> = OwnProps<T>;

const MAX_DROPDOWN_MENU_HEIGHT = 300 * 1.1;

const DROPDOWN_MENU_CONTAINER_CLASSNAME = 'dropdown-menu__scrollable';

interface DropdownStyle {
	top: number | 'auto';
	bottom: number | 'auto';
	left: number;
	width: number | 'auto';
}
const _filterMultiSelectSelection = (selectedValue, valueKey) => (selection) => {
	const _currentValue = selection?.[valueKey ?? 'id'] ?? null;
	const _selected = selectedValue?.[valueKey ?? 'id'] ?? null;

	return _currentValue !== _selected;
};

const Dropdown = <T extends DropdownOptionType>(props: Props<T>) => {

	const {
		defaultValue,
		open,
		onValueChange,
		isControlled,
		onLazyLoad,
		onFocus,
		onMenuOpen,
		fixed,
		filterBy,
		hasBlankOption,
		filterable,
		isMenuItemUnlimited,
		placeholder,
		forcePlaceholder,
		onClear,
		disabled,
		isActionDropdown,
		fullWidth,
		dropdownClassName,
		withCaret,
		isButtonFullWidth,
		isWhite,
		withBorder,
		inputClassName,
		label,
		tooltipMessage,
		className,
		id,
		dropdownId,
		valueKey = 'id',
		containerClassName,
		isMultiselect,
		onMultiselectChange,
		onClose,
		defaultSelections,
		autoApplySelections,
		blankOption,
		hasActionOption,
		shouldScrollToSelectedItem,
		selectBtnLabel,
		getSelectedValue,
	} = props;
	const dropdownToggleRef = React.useRef<HTMLDivElement>(null);
	const searchInputRef = React.useRef<HTMLInputElement>(null);
	const wrapperRef = React.useRef<HTMLDivElement>(null);
	const forceCloseRef = React.useRef(false);

	const [isLazyLoading, setIsLazyLoading] = React.useState(!!onLazyLoad);
	const [dropup, setDropup] = React.useState(false);
	const [isOpen, setIsOpen] = React.useState(props.autoOpen ?? props.open ?? false);
	const [searchText, setSearchText] = React.useState('');
	const [dropdownStyle, setDropdownStyle] = React.useState<DropdownStyle | undefined>(undefined);
	const [selected, setSelected] = React.useState<Nullable<T>>(props.defaultValue ?? null);
	const [openNestedMenuKey, setOpenNestedMenuKey] = React.useState<string | null>(null);
	const [submenuContent, setSubmenuContent] = React.useState<React.JSX.Element | null>(null);

	const [selections, setSelections] = React.useState<Nullable<T>[]>(defaultSelections ?? []); // Nullable in case of explicit "none" value in multiselect dropdown

	const controlledMultiSelect = isMultiselect && props.controlledMultiSelect === true;

	const areSelectionsEqual = React.useCallback((
		a: Nullable<T>[],
		b: Nullable<T>[]
	): boolean => {
		if (a.length !== b.length) {
			return false;
		}

		return a.every((item, index) => {
			const aValue = item !== null ? item[valueKey] : null;
			const bItem = b[index];
			const bValue = bItem !== null ? bItem[valueKey] : null;

			return aValue === bValue;
		});
	}, [valueKey]);

	React.useEffect(() => {
		if (!isMultiselect) {
			return;
		}

		if (controlledMultiSelect) {
			return;
		}

		if (isOpen) {
			return;
		}

		const nextSelections = defaultSelections ?? [];

		setSelections((currentSelections) => {
			if (areSelectionsEqual(currentSelections, nextSelections)) {
				return currentSelections;
			}

			return nextSelections;
		});
	}, [
		areSelectionsEqual,
		defaultSelections,
		isMultiselect,
		isOpen,
		controlledMultiSelect,
	]);

	const currentSelectionsSet = React.useMemo(() => {
		if (isMultiselect) {
			const selectedIds = !!props.controlledMultiSelect
				? props.currentSelections.map((_selected) => _selected !== null ? _selected[valueKey ?? 'id'] : null)
				: selections.map((_selected) => _selected !== null ? _selected[valueKey ?? 'id'] : null);
			return new Set(selectedIds); // using set due to having null as value
		}
	}, [isMultiselect, props, selections, valueKey]);

	const calculatedPropName = React.useMemo(() => {
		return valueKey ?? (props as PropsWithLabel<T>).labelKey;
	}, [valueKey, props]);

	const handleDocumentClick = React.useCallback((event: MouseEvent) => {
		if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
			setOpenNestedMenuKey(null);
		}
	}, []);

	const closeDropdown = React.useCallback(() => {
		forceCloseRef.current = true;
		setIsOpen(false);
	}, []);

	React.useEffect(() => {
		if (!isOpen && isMultiselect) {
			setSelections(defaultSelections ?? []);
		}
	}, [defaultSelections, isMultiselect, isOpen]);

	React.useEffect(() => {
		if (!isOpen) {
			setOpenNestedMenuKey(null);
			setSubmenuContent(null);
		}
	}, [isOpen]);

	React.useEffect(() => {
		document.addEventListener('click', handleDocumentClick);
		return () => document.removeEventListener('click', handleDocumentClick);
	}, [handleDocumentClick]);

	const toggleNestedMenu = React.useCallback((menuKey: string) => {
		setOpenNestedMenuKey((prev) => (prev === menuKey ? null : menuKey));
	}, []);

	React.useEffect(() => {
		setSelected(defaultValue ?? null);
	}, [defaultValue]);

	React.useEffect(() => {
		if (open !== undefined) {
			setIsOpen(open);
		}
	}, [open]);

	const getSectionsMapper = React.useCallback((key: string) => (section: SectionType<T>) => section[key], []);

	const changeValue = React.useCallback(async (selectedValue: string, selectedOption?: Nullable<T>) => {
		if (onValueChange) {
			await onValueChange(selectedOption ?? null, selectedValue);
		}
	}, [onValueChange]);

	const applyMultiselectChanges = React.useCallback(() => {
		if (!isMultiselect) {
			return;
		}
		const targetSelections = props.controlledMultiSelect ? props.currentSelections : selections;
		onMultiselectChange(targetSelections);
		if (!autoApplySelections) {
			closeDropdown();
		}
	}, [autoApplySelections, closeDropdown, isMultiselect, onMultiselectChange, props, selections]);

	const handleChange = React.useCallback((item: string, options: T[]) => {
		const originalItem = options.find((_option) => {
			if (_option[calculatedPropName] === null && item === null) {
				return true;
			}
			return _option[calculatedPropName]?.toString() === item;
		});
		const selectedValue = isControlled ? defaultValue : originalItem;

		if (isMultiselect) {
			const targetSelections = props.controlledMultiSelect ? props.currentSelections : selections;
			const newValues = currentSelectionsSet?.has(selectedValue?.[valueKey ?? 'id'] ?? null)
				? targetSelections.filter(_filterMultiSelectSelection(selectedValue, valueKey))
				: [...(targetSelections ?? []), selectedValue ?? null];

			if (props.controlledMultiSelect) {
				props.setCurrentSelections(newValues);
			} else {
				setSelections(newValues);
			}
			if (autoApplySelections) {
				onMultiselectChange(newValues);
			}
		} else {
			setSelected(selectedValue ?? null);
			changeValue(item, selectedValue);
		}
	}, [autoApplySelections,
		calculatedPropName,
		changeValue,
		currentSelectionsSet,
		defaultValue,
		isControlled,
		isMultiselect,
		selections,
		valueKey,
		onMultiselectChange,
		props,
	]);

	const lazyLoad = React.useCallback(async (lazyLoadedVal: boolean) => {
		if (!onLazyLoad) {
			return;
		}
		setIsLazyLoading(true);
		await onLazyLoad(lazyLoadedVal);
		setIsLazyLoading(false);
	}, [onLazyLoad]);

	const handleFocus = React.useCallback(() => {
		if (onFocus) {
			onFocus();
		}
		let options: T[] | null = null;
		if ('useSectionList' in props) {
			const { sections, sectionOptionsKey } = props;
			const sectionMapper = getSectionsMapper(sectionOptionsKey);
			options = sections.flatMap(sectionMapper);
		} else {
			options = props.options;
		}
		if (onLazyLoad) {
			const lazyLoadedVal = !!options && options.length > 0;
			setIsLazyLoading(true);
			lazyLoad(lazyLoadedVal);
		}
	}, [getSectionsMapper, lazyLoad, onFocus, onLazyLoad, props]);

	const scrollToSelectedItem = React.useCallback(() => {
		if (!shouldScrollToSelectedItem || !getSelectedValue || !id) {
			return;
		}
		window.requestAnimationFrame(() => {
			const dropdown = document.getElementById(id);
			if (!dropdown) {
				throw new Error('Dropdown container not found');
			}

			const menu = dropdown.getElementsByClassName(DROPDOWN_MENU_CONTAINER_CLASSNAME)[0];
			const items = Array.from(menu.children);
			const selectedValue = getSelectedValue();
			const index = items.findIndex((item) => item.textContent === selectedValue);
			const selectedItem = items[index];
			if (selectedItem) {
				const { height } = selectedItem.getBoundingClientRect();
				menu.scrollTo({ top: index * height });
			}
		});
	}, [id, getSelectedValue, shouldScrollToSelectedItem]);

	const handleToggle = React.useCallback((newIsOpen: boolean, event?) => {
		if (isMultiselect && newIsOpen === false && event) {
			const target = event.target as HTMLElement;
			// If the click happened inside the dropdown menu, do not close the dropdown.
			if (target.closest('.dropdown-menu')) {
				return;
			}
		}

		if (newIsOpen) {
			handleFocus();
		}
		if (newIsOpen && dropdownToggleRef.current) {
			const bodyRect = document.body.getClientRects()[0];
			const dropdownRect = dropdownToggleRef.current.getClientRects()[0] || {};
			const dropdownTogglePositionY = dropdownRect.y || 0;
			const isDropup = dropdownTogglePositionY > (bodyRect.height - MAX_DROPDOWN_MENU_HEIGHT);
			if (fixed) {
				const inputRect = wrapperRef.current?.getClientRects()?.[0] ?? { x: 0, y: 0, bottom: 0, width: 0, height: 0 };
				const { x, y, bottom, width, height } = inputRect;

				setDropdownStyle({
					top: isDropup ? 'auto' : bottom,
					bottom: isDropup ? bodyRect.height - y - height : 'auto',
					left: x,
					width: width,
				});
			} else {
				setDropdownStyle(undefined);
			}
			setDropup(isDropup);
		}
		if (open !== undefined) {
			if (open) {
				onMenuOpen?.();
				searchInputRef.current?.focus();
			}
		} else {
			setIsOpen(newIsOpen);
			if (newIsOpen) {
				onMenuOpen?.();
				searchInputRef.current?.focus();
			}
		}
		scrollToSelectedItem();
	}, [fixed, handleFocus, isMultiselect, onMenuOpen, open, scrollToSelectedItem]);

	const onSearchInputChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		setSearchText(e.target.value);
	}, []);

	const clearSearchInput = React.useCallback(() => {
		setSearchText('');
	}, []);

	const defaultRenderOption = React.useCallback((option: T | null): React.JSX.Element => {
		const { labelKey } = props as PropsWithLabel<T>;
		return option === null ? (
			<span className="dropdown-menu__blank-item__label">{blankOption ?? 'None'}</span>
		) : (
			<>{option[labelKey]}</>
		);
	}, [blankOption, props]);

	const filterOption = React.useCallback((option: Nullable<T>): boolean => {
		if (!searchText || (hasBlankOption && !option)) {
			return true;
		}
		const lowerSearch = searchText.toLowerCase();
		if (Array.isArray(filterBy)) {
			const searchWords = lowerSearch.replace(/\s\s+/g, ' ').split(' ');
			const searchableValues: string[] = [];
			for (const field of filterBy) {
				const value = option?.[field];
				if (value && typeof value === 'string') {
					searchableValues.push(value.toLowerCase());
				}
			}
			for (const word of searchWords) {
				let matched = false;
				for (const val of searchableValues) {
					if (val.includes(word)) {
						matched = true;
						break;
					}
				}
				if (!matched) return false;
			}
			return true;
		} else if (typeof filterBy === 'function') {
			return filterBy(option, lowerSearch);
		}
		return true;
	}, [filterBy, hasBlankOption, searchText]);

	const onNestedItemClick = React.useCallback((
		item: T,
		index: number,
		_searchText: string,
		onChangeHandler: (item: string) => void,
		menuKey: string
	) => (e) => {
		e.stopPropagation();
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const target = (e as any).currentTarget as HTMLElement;
		const rect = target.getBoundingClientRect();
		// Toggle submenu: if it's already open for this item, close it;
		// otherwise, open it by setting submenuContent
		if (openNestedMenuKey === menuKey) {
			toggleNestedMenu(menuKey); // close
			setSubmenuContent(null);
		} else {
			const submenuHeight = rect.height;
			toggleNestedMenu(menuKey); // open
			setSubmenuContent(
				<div
					className={`${styles['nested-menu']} ${styles['dropdown-menu--cascade-left']}`}
					style={{
						top: 0 + index * submenuHeight,
					}}
				>
					{item.options.map((nestedItem, nestedIndex: number) =>
						renderMenuItemInternalRef.current(nestedItem, nestedIndex, _searchText, onChangeHandler)
					)}
				</div>
			);
		}
	}, [openNestedMenuKey, toggleNestedMenu]);

	const onCheckboxClick = React.useCallback((itemId: string, onChangeHandler: (item: string) => void) => () => {
		onChangeHandler(itemId);
	}, []);

	const renderMenuItemInternal = React.useCallback((
		item: Nullable<T>,
		index: number,
		_searchText: string,
		onChangeHandler: (item: string) => void
	): React.JSX.Element | null => {
		const { renderMenuItem: customRenderMenuItem = defaultRenderOption } = props as PropsWithCustomRender<T>;
		const isFiltered = !filterable || filterOption(item);
		let _className = isMenuItemUnlimited ? 'dropdown-menu__unlimited-item' : '';
		if (item === null) {
			_className = `${_className} dropdown-menu__blank-item`;
		}
		if (item?.isDivider) {
			return <div className={styles['dropdown-divider']} key={`menuItem#${index + 1}`} />;
		}
		if (!isFiltered) {
			return null;
		}
		if (item && Array.isArray(item.options) && item.options.length > 0) {
			const menuKey = `menuItem#${index + 1}`;
			return (
				<div className={styles['dropdown-nested-item']} key={menuKey}>
					<BootstrapDropdown.Item
						className={`${_className} ${styles['has-submenu']}`}
						disabled={!!item.disabled}
						onClick={onNestedItemClick(item, index, _searchText, onChangeHandler, menuKey)}
					>
						{customRenderMenuItem(item, _searchText, index)}
					</BootstrapDropdown.Item>
				</div>
			);
		}

		const eventKey = item?.[calculatedPropName] as string;
		const idKey = valueKey;
		return (
			<BootstrapDropdown.Item
				className={_className}
				disabled={!!item?.disabled}
				eventKey={eventKey !== undefined ? eventKey : ''}
				key={`menuItem#${index + 1}`}
				onClick={item?.onClick ?? undefined}
				onSelect={onChangeHandler}
			>
				<div className={'dropdown-item__content'}>
					{item === null ? defaultRenderOption(null) : customRenderMenuItem(item, searchText, index)}
					{
						isMultiselect &&
						<Checkbox
							checked={!!currentSelectionsSet?.has(item !== null ? item?.[valueKey] : null)}
							onChange={onCheckboxClick(`${item?.[idKey] ?? ''}`, onChangeHandler)}
						/>
					}
				</div>
			</BootstrapDropdown.Item>
		);
	}, [
		calculatedPropName,
		defaultRenderOption,
		filterOption,
		filterable,
		isMenuItemUnlimited,
		isMultiselect,
		props,
		searchText,
		onNestedItemClick,
		onCheckboxClick,
		valueKey,
		currentSelectionsSet,
	]);

	// We need to store it in ref to avoid circular dependency between renderMenuItemInternal and onNestedItemClick
	const renderMenuItemInternalRef = React.useRef(renderMenuItemInternal);

	React.useEffect(() => {
		renderMenuItemInternalRef.current = renderMenuItemInternal;
	}, [renderMenuItemInternal]);

	const renderMenuItemFn = React.useCallback((
		item: Nullable<T>,
		index: number,
		_searchText: string,
		onChangeHandler: (item: string) => void
	): React.JSX.Element | null => {
		return renderMenuItemInternal(item, index, _searchText, onChangeHandler);
	},
		[renderMenuItemInternal]
	);

	const renderSelected = React.useCallback((): React.JSX.Element => {
		if (forcePlaceholder || isNullOrUndefined(selected)) {
			return <span className="dropdown-toggle__placeholder">{placeholder}</span>;
		}
		if ('renderMenuItem' in props) {
			const { renderSelected: _renderSelected, renderMenuItem: customRenderMenuItem } = props;
			const renderItem = _renderSelected ?? customRenderMenuItem;
			if (renderItem) {
				return renderItem(selected);
			} else {
				// eslint-disable-next-line no-console
				console.warn(
					'[Dropdown]: `renderMenuItem` is used as a prop but value is undefined, falling back to `labelKey`'
				);
			}
		}
		if ('labelKey' in props && props.labelKey) {
			const { labelKey } = props;
			return (selected)[labelKey];
		}
		console.error('[Dropdown]: Cannot render selected without `renderMenuItem` or `labelKey` defined');
		return <></>;
	}, [forcePlaceholder, placeholder, props, selected]);

	const renderClearButton = React.useCallback((): React.JSX.Element | null => {
		if (!onClear || !selected) {
			return null;
		}
		return <span className={`icon-close dropdown-toggle__clear ${disabled ? 'disabled' : ''}`} onClick={onClear} />;
	}, [disabled, onClear, selected]);

	const renderCloseButton = React.useCallback((): React.JSX.Element | null => {
		if (!onClose) {
			return null;
		}
		return <span className={'dropdown-menu__close-icon icon-close'} onClick={onClose} />;
	}, [onClose]);

	const dropdownToggleClassName = React.useMemo(() => {
		return bemBlock('dropdown-toggle', {
			'with-caret': !!withCaret,
			'icon-down': !!withCaret,
			'fullwidth-button': !!isButtonFullWidth,
			'lazy-loading': !!isLazyLoading,
			loading: !!isLazyLoading,
			action: !!isActionDropdown,
			'with-caret__with-close': !!withCaret && !!onClose,
		});
	}, [withCaret, isButtonFullWidth, isLazyLoading, isActionDropdown, onClose]);

	const dropdownToggleContainerClassName = React.useMemo(() => {
		const bemResult = bemBlock('dropdown-toggle', {
			bordered: !!withBorder && !isActionDropdown,
			white: !!isWhite,
		});

		return `${inputClassName ?? ''} ${bemResult} ${isActionDropdown ? styles['actions-3dot'] : ''}`;
	}, [isWhite, isActionDropdown, inputClassName, withBorder]);

	const onToggleClick = React.useCallback(() => {
		if (isOpen && isMultiselect) {
			closeDropdown();
		}
	}, [isOpen, isMultiselect, closeDropdown]);

	const renderToggle = React.useCallback((): React.JSX.Element => {
		if (isActionDropdown) {
			return <span className={`icon-actions ${styles['actions-icon']}`} ref={dropdownToggleRef} />;
		}
		return (
			<div className={`${dropdownToggleClassName}`}>
				<div
					className="dropdown-menu__selected"
					onClick={onToggleClick}
					ref={dropdownToggleRef}
				>
					<span className="dropdown-menu__selected-text">{renderSelected()}</span>
					{withCaret && <span className="icon-down dropdown-menu__with-caret"></span>}
				</div>
				{renderClearButton()}
			</ div>
		);
	}, [
		isActionDropdown,
		dropdownToggleClassName,
		onToggleClick,
		renderSelected,
		withCaret,
		renderClearButton,
	]);

	const dropdownMenuClassName = React.useMemo(() => {
		return bemBlock('dropdown-menu', {
			'scroll-container': true,
			'full-width': !!fullWidth,
			fixed: !!fixed,
			...(dropdownClassName ? { [dropdownClassName]: true } : {}),
		});
	}, [fullWidth, fixed, dropdownClassName]);

	const renderNoResults = React.useCallback(() => {
		return (
			<BootstrapDropdown.Item
				className={styles['no-results']}
				disabled={true}
			>
				No results found
			</BootstrapDropdown.Item>
		);
	}, []);

	const renderLoading = React.useCallback(() => {
		return (
			<BootstrapDropdown.Item
				className={styles['loading-results']}
				disabled={true}
			>
				Loading...
			</BootstrapDropdown.Item>
		);
	}, []);

	const renderMenuList = React.useCallback((): React.JSX.Element => {
		if ('useSectionList' in props) {
			const { useSectionList } = props;
			return (
				<SectionListMenu
					calculatedPropName={calculatedPropName}
					defaultValue={defaultValue}
					filterable={!!filterable}
					hasBlankOption={!!hasBlankOption}
					isLazyLoading={isLazyLoading}
					onChange={handleChange}
					renderMenuItem={renderMenuItemFn}
					renderSectionHeader={props.renderSectionHeader}
					searchText={searchText}
					sectionOptionsKey={props.sectionOptionsKey}
					sections={props.sections}
					sectionTitleKey={props.sectionTitleKey}
					selected={selected}
					useSectionList={useSectionList}
				/>
			);
		} else {
			const { filterOptions, options } = props;
			return (
				<FlatListMenu
					calculatedPropName={calculatedPropName}
					defaultValue={defaultValue}
					filterOptions={filterOptions}
					hasBlankOption={!!props.hasBlankOption}
					isLazyLoading={isLazyLoading}
					onChange={handleChange}
					options={options}
					renderLoading={renderLoading}
					renderMenuItem={renderMenuItemFn}
					renderNoResults={renderNoResults}
					searchText={searchText}
					selected={selected}
				/>
			);
		}
	}, [
		props,
		calculatedPropName,
		defaultValue,
		filterable,
		hasBlankOption,
		isLazyLoading,
		handleChange,
		renderMenuItemFn,
		searchText,
		selected,
		renderNoResults,
		renderLoading,
	]);

	const renderMenuFooter = React.useCallback((): Nullable<React.JSX.Element> => {
		if (!isMultiselect || autoApplySelections) {
			return null;
		}

		return (
			<Button
				customStyleClass="dropdown-menu__footer-button"
				label={selectBtnLabel ? selectBtnLabel : 'Apply Filter'}
				onClick={applyMultiselectChanges}
				style="primary"
			/>
		);
	}, [applyMultiselectChanges, autoApplySelections, isMultiselect, selectBtnLabel]);

	const selectAll = React.useCallback(() => {
		if (!isMultiselect) {
			return;
		}
		const { options } = props as FlatListProps<T>;
		const filteredOptions = filterable
			? options?.filter((opt) => filterOption(opt)) ?? []
			: options ?? [];

		props.controlledMultiSelect ? props.setCurrentSelections(filteredOptions) : setSelections(filteredOptions);
	}, [filterOption, filterable, isMultiselect, props]);

	const clearAll = React.useCallback(() => {
		if (!isMultiselect) {
			return;
		}

		props.controlledMultiSelect ? props.setCurrentSelections([]) : setSelections([]);
		setSearchText('');
	}, [isMultiselect, props]);

	const renderMultiselectOptions = React.useCallback(() => {
		if (!isMultiselect) {
			return null;
		}
		const targetSelections = props.controlledMultiSelect ? props.currentSelections : selections;
		const selectedLength = targetSelections?.length;
		return (
			<div className="multiselect-options">
				<div className="multiselect-options__count">
					Selected ({selectedLength})
				</div>
				<div className="multiselect-options__controls">
					<Button
						label="Select all"
						onClick={selectAll}
						size="small"
						style="link"
					/>
					<Button
						label="Clear all"
						onClick={clearAll}
						size="small"
						style="link"
					/>
				</div>
			</div>
		);
	}, [clearAll, isMultiselect, props, selectAll, selections]);

	React.useEffect(() => {
		if (isOpen && filterable) {
			searchInputRef.current?.focus();
		}
	}, [isOpen, filterable]);

	const onAction = React.useCallback(() => {
		if (hasActionOption && props.actionText && props.onActionClick !== undefined) {
			props.onActionClick();
		}
	}, [hasActionOption, props]);

	return (
		<>
			{label && (
				<div className="input-header">
					<Label label={label} tooltipMessage={tooltipMessage} tooltipPlacement="top" withMargin={true} />
				</div>
			)}
			<div className={containerClassName} ref={wrapperRef}>

				<BootstrapDropdown
					className={className}
					drop={dropup ? 'up' : undefined}
					id={id}
					onToggle={handleToggle}
					show={isOpen}

				>
					<BootstrapDropdown.Toggle
						className={dropdownToggleContainerClassName}
						disabled={disabled}
					>
						{renderToggle()}
						{renderCloseButton()}
					</BootstrapDropdown.Toggle>
					<BootstrapDropdown.Menu className={dropdownMenuClassName} id={dropdownId} style={dropdownStyle}>
						{filterable && (
							<a className="dropdown-item dropdown-menu__search-input-container">
								<input
									className="dropdown-menu__search-input"
									onChange={onSearchInputChange}
									placeholder="Search"
									ref={searchInputRef}
									type="text"
									value={searchText}
								/>
								{searchText ? (
									<span className="dropdown-menu__icon icon-close" onClick={clearSearchInput} role="button" />
								) : (
									<span className="dropdown-menu__icon icon-search" />
								)}
							</a>
						)}
						{hasActionOption &&
							<>
								<div className="dropdown-menu__sticky-option" onClick={onAction}>{props.actionText}</div>
								<hr />
							</>
						}
						{
							isMultiselect &&
							<>
								{renderMultiselectOptions()}
							</>
						}
						<div className={DROPDOWN_MENU_CONTAINER_CLASSNAME}>
							{renderMenuList()}
						</div>
						{submenuContent}
						{renderMenuFooter()}
					</BootstrapDropdown.Menu>
				</BootstrapDropdown>
			</div>
		</>
	);
};

export default React.memo(Dropdown) as <T extends DropdownOptionType>(
	props: Props<T>
) => React.JSX.Element;
